/**
 * CSV → YAML Tool
 *
 * Converts CSV to YAML by parsing with PapaParse and serializing with the yaml library.
 */

import * as Papa from 'papaparse';
import { stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const CsvToYamlTool: ITransformerTool = {
  id: 'csv-to-yaml',
  name: 'CSV → YAML',
  description: 'Convert CSV to YAML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'yaml', 'convert', 'transform', 'tabular'],

  seo: {
    slug: 'csv-to-yaml',
    pageTitle: 'CSV to YAML Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert CSV data to YAML format instantly. Supports header rows and configurable indentation. Fast, private, runs entirely in your browser.',
    keywords: ['csv to yaml', 'csv to yaml converter', 'convert csv to yaml online', 'csv yaml transformer'],
    relatedTools: ['yaml-to-csv', 'csv-to-json', 'json-to-yaml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.CSV],
  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'hasHeader',
      type: 'boolean',
      label: 'Header Row',
      defaultValue: true,
      description: 'Treat first row as column headers',
    },
    {
      name: 'indent',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['2', '4'],
      description: 'Number of spaces for YAML indentation',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const hasHeader = config.hasHeader !== false;
    const indent = parseInt((config.indent as string) || '2', 10);

    let data: unknown;
    try {
      if (hasHeader) {
        const result = Papa.parse<Record<string, string>>(input, {
          header: true,
          skipEmptyLines: true,
          transform: (value) => value.trim(),
        });
        if (result.errors.length > 0) {
          throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
        }
        data = result.data;
      } else {
        const result = Papa.parse<string[]>(input, {
          header: false,
          skipEmptyLines: true,
          transform: (value) => value.trim(),
        });
        if (result.errors.length > 0) {
          throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
        }
        data = result.data;
      }
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const msg = e instanceof Error ? e.message : 'Invalid CSV';
      throw ToolError.validation(`Invalid CSV: ${msg}`, {
        suggestion: 'Check quotes and delimiters; ensure rows have consistent columns',
      });
    }

    try {
      return stringifyYaml(data, { indent });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to serialize YAML';
      throw ToolError.execution(msg);
    }
  },
};
